import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { App, Form, Progress } from "antd";
import socket from "utils/socket";
import useTimer from "hooks/useTimer";
import { formatMomentDurationFull } from "utils/helpers";
import { apiGetModelsList } from "api/apiAiModels";
import { apiGetAllSymbols } from "api/apiSymbols";
import {
  apiRunStrategiesExplorer,
  apiCancelStrategiesExplorer,
} from "api/apiPredictions";
import ExplorerSetup from "./ExplorerSetup";
import ExplorerResults, { annotateRow } from "./ExplorerResults";

// Strategies Explorer: thousands of backtests (symbols x model sets x
// parameter grid) on the server's batch engine, ranked by how they hold up
// after a holdout split. Results live in this page only (not saved).

const STORAGE_KEY = "strategiesExplorerParams";
const DEFAULT_START_DATE = "2018-01-01";

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const save = (values) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // storage unavailable/full -- remembering the form is only a convenience
  }
};

const numbers = (values) =>
  [...new Set((values || []).map(Number).filter((v) => Number.isFinite(v)))];

const StrategiesExplorer = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const userPremiumFee = useSelector(
    (state) => state.main.user.preferences.premiumFee,
  );

  const [modelsList, setModelsList] = useState([]);
  const [symbolsList, setSymbolsList] = useState([]);
  const [results, setResults] = useState([]);
  const [run, setRun] = useState(null);
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const { startTimer, stopTimer, time } = useTimer();
  const activeUuid = useRef(null);

  const [initialValues] = useState(() => {
    const saved = loadSaved();
    const start = dayjs(saved.startDate || DEFAULT_START_DATE);
    const end = dayjs();
    return {
      allSymbols: saved.allSymbols ?? true,
      symbols: saved.symbols,
      allModels: saved.allModels ?? false,
      modelIds: saved.modelIds,
      comboSizes: saved.comboSizes ?? [1],
      dates: [start, end],
      // end date is always today, so the split is re-derived, not restored
      splitDate: start.add(Math.round(end.diff(start, "day") * 0.7), "day"),
      takeProfitPercent: saved.takeProfitPercent ?? [2, 3, 4, 5],
      stopLossPercent: saved.stopLossPercent ?? [0, -8, -12],
      daysOpenExitCount: saved.daysOpenExitCount ?? [8, 12, 20],
      initialBalance: saved.initialBalance ?? 10000,
      orderSize: saved.orderSize ?? 100,
      orderSizeType: saved.orderSizeType ?? "percent",
      maxOpenOrders: saved.maxOpenOrders ?? 1,
      userCommission: saved.userCommission ?? userPremiumFee ?? 1,
      premiumPerShare: saved.premiumPerShare ?? 0,
      slippage: saved.slippage ?? 0,
      enterPositionBuyCount: saved.enterPositionBuyCount ?? 1,
      exitPositionNoBuyCount: saved.exitPositionNoBuyCount ?? 0,
      minTrades: saved.minTrades ?? 10,
      resetTrendAfterSell: saved.resetTrendAfterSell ?? true,
      excludeCorrupted: saved.excludeCorrupted ?? true,
    };
  });

  useEffect(() => {
    document.title = "Strategies Explorer | Dindicator Dashboard";
    apiGetModelsList("1d").then(setModelsList);
    apiGetAllSymbols().then((list) =>
      setSymbolsList(list.filter((symbol) => symbol.enabled)),
    );
  }, []);

  // leaving the page stops the server-side job
  useEffect(
    () => () => {
      if (activeUuid.current) {
        socket.off(activeUuid.current);
        apiCancelStrategiesExplorer(activeUuid.current).catch(() => {});
      }
    },
    [],
  );

  const modelNames = useMemo(
    () => Object.fromEntries(modelsList.map((m) => [m._id, m.name])),
    [modelsList],
  );

  const handleRun = async (values) => {
    const startDate = values.dates[0].format("YYYY-MM-DD");
    const endDate = values.dates[1].format("YYYY-MM-DD");
    const { dates, splitDate, ...rest } = values;
    save({ ...rest, startDate });

    const base = {
      initialBalance: values.initialBalance,
      orderSize: values.orderSize,
      orderSizeType: values.orderSizeType,
      maxOpenOrders: values.maxOpenOrders,
      userCommission: values.userCommission,
      premiumPerShare: values.premiumPerShare,
      slippage: values.slippage,
      enterPositionBuyCount: values.enterPositionBuyCount,
      exitPositionNoBuyCount: values.exitPositionNoBuyCount,
      resetTrendAfterSell: values.resetTrendAfterSell,
    };
    const runSettings = {
      startDate,
      endDate,
      splitDate: splitDate ? splitDate.format("YYYY-MM-DD") : null,
      base,
    };

    const uuid = uuidv4();
    activeUuid.current = uuid;
    setResults([]);
    setSummary(null);
    setRun(runSettings);
    setProgress({ tested: 0, total: 0, percent: 0 });
    setIsPending(true);
    startTimer();

    socket.on(uuid, (data) => {
      const rows = (data.results || []).map(annotateRow);
      if (data.replace) {
        // past the row limit the server sends its best rows to keep instead
        setResults(rows);
      } else if (rows.length) {
        setResults((prev) => prev.concat(rows));
      }
      setProgress({
        tested: data.tested,
        total: data.total,
        percent: data.progress,
      });
    });

    try {
      const runSummary = await apiRunStrategiesExplorer({
        uuid,
        symbols: values.allSymbols ? [] : values.symbols,
        modelIds: values.allModels ? [] : values.modelIds,
        comboSizes: values.comboSizes,
        axes: {
          takeProfitPercent: numbers(values.takeProfitPercent),
          stopLossPercent: numbers(values.stopLossPercent),
          daysOpenExitCount: numbers(values.daysOpenExitCount),
        },
        base,
        ...runSettings,
        minTrades: values.minTrades,
        excludeCorrupted: values.excludeCorrupted,
      });
      setSummary(runSummary);
      setProgress((p) => ({ ...p, percent: 100 }));
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Strategies Explorer run failed",
      );
    } finally {
      // let the last socket batch land before unsubscribing
      setTimeout(() => socket.off(uuid), 1500);
      if (activeUuid.current === uuid) activeUuid.current = null;
      setIsPending(false);
      stopTimer();
    }
  };

  const handleCancel = () => {
    if (activeUuid.current) apiCancelStrategiesExplorer(activeUuid.current);
  };

  const eta =
    isPending && progress?.percent > 0 && time
      ? (100 / progress.percent) * time - time
      : null;

  return (
    <>
      <h2>Strategies Explorer</h2>
      <p className="mb-4" style={{ opacity: 0.75 }}>
        Backtest many symbols, model sets and exit parameters at once, then
        keep the ones that also hold up after the holdout date.
      </p>
      <div className="cards-wrapper">
        <ExplorerSetup
          form={form}
          initialValues={initialValues}
          modelsList={modelsList}
          symbolsList={symbolsList}
          isPending={isPending}
          onRun={handleRun}
          onCancel={handleCancel}
        />

        {progress && (
          <div className="table-card text-left" style={{ marginBottom: 16 }}>
            <Progress
              percent={progress.percent}
              status={isPending ? "active" : "normal"}
            />
            <span style={{ opacity: 0.8 }}>
              {progress.tested.toLocaleString()} /{" "}
              {progress.total.toLocaleString()} backtests
              {time ? ` -- ${time}s` : ""}
              {eta ? `, about ${formatMomentDurationFull(eta * 1000)} left` : ""}
            </span>
          </div>
        )}

        {(results.length > 0 || summary) && (
          <ExplorerResults
            results={results}
            run={run}
            summary={summary}
            modelNames={modelNames}
          />
        )}
      </div>
    </>
  );
};

export default StrategiesExplorer;
